import React, { useRef, useState } from 'react';
import Button from '../../common/Button/Button';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import ScopeForm from '../ScopeForm/ScopeForm';
import { uploadProjectScopeFile } from '../../../services/projectService';
import styles from './ScopeUpload.module.css';

const ACCEPTED_EXTENSIONS = ['.docx', '.pdf'];
const MAX_FILE_MB = 10;

/**
 * ScopeUpload - two-step scope submission.
 *
 * Step 1: mentor uploads a .docx/.pdf scope document. It is stored in Supabase
 *         Storage and parsed by Gemini into the structured scope schema.
 * Step 2: the extracted data prefills ScopeForm so the mentor can correct
 *         anything before submitting for approval.
 */
const ScopeUpload = ({ projectId, onSubmit, onCancel }) => {
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const [extraction, setExtraction] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const validate = (candidate) => {
    const name = candidate.name.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext))) {
      return 'Only .docx and .pdf files are supported';
    }
    if (candidate.size > MAX_FILE_MB * 1024 * 1024) {
      return `File is too large (max ${MAX_FILE_MB} MB)`;
    }
    if (candidate.size === 0) {
      return 'This file is empty';
    }
    return '';
  };

  const selectFile = (candidate) => {
    if (!candidate) return;
    const validationError = validate(candidate);
    setError(validationError);
    setFile(validationError ? null : candidate);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    selectFile(e.dataTransfer.files?.[0]);
  };

  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    setError('');
    try {
      const result = await uploadProjectScopeFile(projectId, file);
      setExtraction(result);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not read this document');
    } finally {
      setExtracting(false);
    }
  };

  const handleReset = () => {
    setExtraction(null);
    setFile(null);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFormSubmit = (scopeJson) => {
    onSubmit(scopeJson, {
      scope_file_path: extraction.scope_file_path,
      scope_file_name: extraction.scope_file_name
    });
  };

  // --- Step 2: review & edit ---
  if (extraction) {
    return (
      <div className={styles.reviewStep}>
        <div className={styles.reviewBanner}>
          <div>
            <strong className={styles.reviewTitle}>Complete the remaining scope details</strong>
            <p className={styles.reviewNote}>
              We filled {extraction.extracted_field_count} field{extraction.extracted_field_count === 1 ? '' : 's'} from{' '}
              <span className={styles.fileName}>{extraction.scope_file_name}</span>. Only the fields still
              needing your input are shown below — fill them in, then submit.
            </p>
          </div>
          <Button type="button" size="small" variant="outline" onClick={handleReset}>
            Upload a different file
          </Button>
        </div>

        <ScopeForm
          initialValues={extraction.scope}
          onSubmit={handleFormSubmit}
          onCancel={onCancel}
          remainingOnly
        />
      </div>
    );
  }

  // --- Step 1: upload ---
  if (extracting) {
    return (
      <div className={styles.extracting}>
        <LoadingSpinner />
        <p className={styles.extractingTitle}>Reading {file?.name}</p>
        <p className={styles.extractingNote}>
          Extracting the scope details — this usually takes a few seconds.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.uploadStep}>
      <div
        className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".docx,.pdf"
          className={styles.hiddenInput}
          onChange={(e) => selectFile(e.target.files?.[0])}
        />
        <span className={styles.dropIcon}>📄</span>
        {file ? (
          <>
            <p className={styles.fileName}>{file.name}</p>
            <p className={styles.dropHint}>{(file.size / 1024).toFixed(0)} KB — click to choose another file</p>
          </>
        ) : (
          <>
            <p className={styles.dropTitle}>Drop your scope document here, or click to browse</p>
            <p className={styles.dropHint}>Word (.docx) or PDF, up to {MAX_FILE_MB} MB</p>
          </>
        )}
      </div>

      {error && <p className={styles.error}>{error}</p>}

      <p className={styles.helpText}>
        The document is stored with the project, and its details are extracted into the standard
        scope format for you to review before submitting.
      </p>

      <div className={styles.actions}>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="button" onClick={handleExtract} disabled={!file}>
          Upload & Extract
        </Button>
      </div>
    </div>
  );
};

export default ScopeUpload;
